
import { useCallback } from "react";
import { useAuth } from "@/components/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PixelFormValues } from "@/components/dashboard/pixel/CreatePixelForm";
import { Pixel } from "@/types/database";
import { usePixelState, PixelStateCallbacks } from "./usePixelState";
import { usePixelLifecycle } from "./usePixelLifecycle";

export function usePixelCreation(callbacks: PixelStateCallbacks = {}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const pixelState = usePixelState(callbacks);
  const pixelLifecycle = usePixelLifecycle(pixelState);
  
  const {
    setPixelId,
    setSubmittingState,
    setInstallationState,
    resetState,
    completeReset,
    timeoutRef,
    pixelCreatedRef,
    stateTransitionRef
  } = pixelState;

  const createPixel = async (data: PixelFormValues) => {
    if (!user) return;
    
    setSubmittingState(true);
    console.log("Starting pixel creation...");
    
    stateTransitionRef.current = {
      isSettingPixelId: false,
      lastOperation: "create_pixel_started",
      timestamp: Date.now()
    };

    const cleanDomain = data.domain
      .replace(/^https?:\/\//, '')
      .replace(/\/+$/, '');
      
    try {
      // First, check if the user belongs to an organization
      let organizationId = null;
      
      // Get organization owned by the user
      const { data: ownedOrgData, error: ownedOrgError } = await supabase
        .from('organizations')
        .select('id')
        .eq('created_by', user.id);
      
      if (ownedOrgError) {
        console.error('Error fetching owned organization:', ownedOrgError);
      }
      
      if (ownedOrgData && ownedOrgData.length > 0) {
        organizationId = ownedOrgData[0].id;
        console.log('Found user owned organization:', organizationId);
      } else {
        // Check if the user is a member of an organization
        const { data: teamMemberData, error: teamMemberError } = await supabase
          .from('team_members')
          .select('organization_id')
          .eq('user_id', user.id)
          .eq('status', 'active');
          
        if (teamMemberError) {
          console.error('Error checking team membership:', teamMemberError);
        }
        
        if (teamMemberData && teamMemberData.length > 0) {
          organizationId = teamMemberData[0].organization_id;
          console.log('Found user organization from team membership:', organizationId);
        }
      }

      const { data: pixel, error } = await supabase
        .from('pixels')
        .insert({
          name: data.name,
          domain: cleanDomain,
          user_id: user.id,
          organization_id: organizationId
        } as Pixel)
        .select()
        .single();

      if (error) throw error;
      if (!pixel) throw new Error('No pixel returned');

      console.log("Pixel created:", pixel.id, "with organization:", organizationId);
      
      // Use a more reliable approach to update the pixel ID
      pixelCreatedRef.current = true;
      
      stateTransitionRef.current = {
        isSettingPixelId: true,
        lastOperation: "pixel_created",
        timestamp: Date.now()
      };
      
      // Add a small delay to ensure all state updates are processed in order
      await new Promise(resolve => setTimeout(resolve, 100));
      
      console.log("Setting pixel ID after creation:", pixel.id);
      // Only then set the pixel ID which will trigger the installation phase
      setPixelId(pixel.id);
      
      queryClient.invalidateQueries({ queryKey: ['pixels'] });
      toast.success("Pixel created successfully!");
    } catch (error) {
      console.error("Pixel creation error:", error);
      toast.error("Failed to create pixel");
      pixelCreatedRef.current = false;
      
      stateTransitionRef.current = {
        isSettingPixelId: false,
        lastOperation: "pixel_creation_error",
        timestamp: Date.now()
      };
    } finally {
      console.log("Completed pixel creation");
      setSubmittingState(false);
    }
  };

  const testPixel = useCallback(async () => {
    try {
      toast.success("Test request sent successfully!");
      
      // Release force-keep after a longer delay
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    } catch (error) {
      toast.error("Failed to test pixel");
    }
  }, []);

  // Add a method to allow manual closing of the dialog from the installation phase
  const allowClose = useCallback(() => {
    console.log("Allowing dialog closure from installation phase - with improved cleanup sequence");
    
    // Prevent any pending timeouts from interfering
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // Complete cleanup with ordered sequence of operations
    // Step 1: Mark pixel as not created to prevent re-entry into installation phase
    pixelCreatedRef.current = false;
    
    // Step 2: Immediate state updates to prevent submission blocking
    setSubmittingState(false);
    
    // Step 3: Disable installation phase
    setInstallationState(false);
    
    // Step 4: With a small delay to ensure correct order, reset the pixel ID last
    setTimeout(() => {
      console.log("Final cleanup: resetting pixel ID");
      setPixelId(null);
      
      stateTransitionRef.current = {
        isSettingPixelId: false,
        lastOperation: "reset_after_manual_close",
        timestamp: Date.now()
      };
    }, 100);
    
    stateTransitionRef.current = {
      isSettingPixelId: false,
      lastOperation: "manual_allow_close",
      timestamp: Date.now()
    };
  }, [setInstallationState, setSubmittingState, setPixelId]);

  // NEW: Complete cleanup after dialog is closed
  const completeCleanup = useCallback(() => {
    console.log("Performing complete cleanup after dialog close");
    completeReset();
  }, [completeReset]);

  return {
    pixelId: pixelState.pixelId,
    isSubmitting: pixelState.isSubmitting,
    installationPhase: pixelState.installationPhase,
    createPixel,
    testPixel,
    resetPixelId: resetState,
    allowClose,
    completeCleanup
  };
}
